import React from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { useJobs, useCategories } from "../hooks/useApi";
import { useQuery } from "@tanstack/react-query";
import Banner from "./Banner";
import banner from "../assets/img/single_page_header.png"
const Home = () => {
  const navigate = useNavigate();
  
  // Use React Query hooks
  const { data: jobsData, isLoading: jobsLoading } = useJobs();
  const { data: categoriesData, isLoading: categoriesLoading } = useCategories();

  // Fetch companies with React Query
  const fetchCompanies = async () => {
    const response = await fetch('https://ghanish.in/company_listview/');
    if (!response.ok) {
      throw new Error(`Error fetching companies: ${response.status}`);
    }
    return response.json();
  };

  const { data: companies = [], isLoading: companyLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: fetchCompanies,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 2,
    onError: (err) => {
      console.error("Error fetching companies:", err);
    }
  });

  // Helper function to get icon based on category name
  const getCategoryIcon = (categoryName) => {
    switch(categoryName.toLowerCase()) {
      case 'wfh': return 'fa-house';
      case 'internship': return 'fa-graduation-cap';
      case 'drive': return 'fa-car';
      case 'batches': return 'fa-users';
      default: return 'fa-briefcase';
    }
  };

  // Process jobs data
  const jobs = jobsData?.map(job => ({
    id: job.job_id,
    jobTitle: job.title || '',
    jobDescription: job.description || '',
    jobPosition: job.position || '',
    skills: [],
    jobType: job.position || 'Full Time',
    package: job.package ? (job.package).toString() : '0',
    location: job.location || 'Not Specified',
    category: job.category ? job.category.toString() : '',
    companyDetails: job.company ? job.company.toString() : '',
    image: '',
    job_code: job.job_code || '',
    location_name: job.location_name || '',
    company_id: job.company_id || '',
    company_name: job.company_name || '',
    company_image_url: job.company_image_url || ''

  })) || [];

  // Process categories data
  const categories = categoriesData ? Object.entries(categoriesData).map(([key, value]) => ({
    value: key,
    displayName: value,
    icon: getCategoryIcon(value),
    urlSlug: value.toLowerCase().replace(/\s+/g, '-'),
    jobs: jobsData?.filter(job => job.category.toString() === key).length || 0
  })) : [];

  const handleCategoryClick = (urlSlug) => {
    navigate(`/category/${urlSlug}`);
  };

  const slugify = (str) =>
    str.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w\-]+/g, '');
  
  const handleCompanyClick = (companyId, companyName) => {
    const slug = slugify(companyName);
    navigate(`/company/${slug}`);
  };
  
  

  const handleJobClick = (jobId, jobTitle, jobCode) => {
    // If job code is available, use the short URL format
    if (jobCode) {
      navigate(`/jobs/${jobCode}`);
      return;
    }
    
    // If job title is available, create a URL-friendly slug
    if (jobTitle) {
      const slugifiedTitle = jobTitle
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w\-]+/g, '')
        .replace(/\-\-+/g, '-')
        .replace(/^-+/, '')
        .replace(/-+$/, '');
      
      if (slugifiedTitle) {
        navigate(`/jobs/${slugifiedTitle}`);
        return;
      }
    }
    
    // Fallback to the original URL format
    const formattedTitle = jobTitle ? jobTitle.toLowerCase().replace(/\s+/g, '+') : 'job';
    navigate(`/job-detail/${formattedTitle}/${jobId}`);
  };

  const isLoading = jobsLoading || categoriesLoading || companyLoading;

  // Function to chunk the jobs array into specified sizes
  const chunkArray = (array, size) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  };

  // Create job chunks of 20 jobs each
  const jobChunks = chunkArray(jobs, 20);
  // Create company chunks of 6 companies each
  const companyChunks = chunkArray(companies, 12);

  // Function to render job chunk with 5 jobs per row
  const renderJobChunk = (jobChunk, chunkIndex) => {
    // Create rows of 5 jobs each
    const jobRows = chunkArray(jobChunk, 5);
    
    return (
      <section className="padd-top-80 padd-bot-80" key={`job-section-${chunkIndex}`}>
        <div className="container">
          <div className="row mb-4">
            <div className="col-12 text-center">
              <div className="heading">
                <h2>{chunkIndex === 0 ? 'Latest Job Openings' : 'More Job Opportunities'}</h2>
                <p>Find the perfect position for your next career move</p>
              </div>
            </div>
          </div>
          
          {jobRows.map((row, rowIndex) => (
            <div className="row" key={`job-row-${chunkIndex}-${rowIndex}`}>
              {row.map((job) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg custom-desktop-col mb-4" key={job.id}>

                  <div
                    className="utf_grid_job_widget_area pt-0"
                    onClick={() => handleJobClick(job.id, job.jobTitle, job.job_code)}
                    style={{ cursor: "pointer" }}
                  >
                    <div
                      className="u-content px-0"
                      style={{ position: "relative" }}
                    >
                      <div
                        className="avatar-square box-80"
                        style={{ width: "100%" }}
                      >
                        <a
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleJobClick(job.id, job.jobTitle, job.job_code);
                          }}
                        >
                          <div
                            className="category-icon-fallback"
                            style={{
                              width: "100%",
                              height: "200px",
                              backgroundColor: "#f8f9fa",
                              borderRadius: "8px",
                              position: "relative",
                              overflow: "hidden"
                            }}
                          >
                            {/* Default fallback icon - shown when no image URL */}
                            <i
                              className="fa-solid fa-house"
                              style={{
                                fontSize: "3.5rem",
                                color: "#ccc",
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                zIndex: 1,
                                display: job.company_image_url?.trim() ? "none" : "block"
                              }}
                            ></i>

                            {/* Company image */}
                            {job.company_image_url?.trim() && (
                              <img
                                className="img-responsive"
                                src={job.company_image_url}
                                alt={job.company_name}
                                loading="lazy"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  const fallbackIcon = e.target.previousSibling;
                                  if (fallbackIcon) {
                                    fallbackIcon.style.display = "block";
                                  }
                                }}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  borderRadius: "8px",
                                  zIndex: 2
                                }}
                              />
                            )}
                          </div>

                         
 


                        </a>
                      </div>
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          zIndex: 3,
                        }}
                      >
                        <span className="job-type bg-success border-1 text-white position-absolute top-2 start-2">
                          {categories.find(
                            (cat) => cat.value === job.category
                          )?.displayName || job.category}
                        </span>
                      </div>
                      <h5 className="pt-3 px-3 text-capitalize">
                        <a
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleJobClick(job.id, job.jobTitle, job.job_code);
                          }}
                        >
                          {job.jobTitle}
                        </a>
                      </h5>
                      <p className="text-muted mb-0">
                        <i className="fa fa-briefcase text-capitalize"></i>{" "}
                        {job.jobPosition}
                      </p>
                      <p className="text-muted mb-0">
                        <i className="fa fa-industry text-capitalize"></i>{" "}
                        {job.company_name}
                      </p>
                   
                        <p className="text-muted py-0 my-0">
                          <i className="fas fa-wallet"></i> {job.package}+ LPA
                        </p>
                        <p className="text-muted py-0 my-0 text-capitalize">
                          <i className="fas fa-map-marker-alt"></i> {job.location_name}
                        </p>
                        <p className="text-muted py-0 my-0 text-capitalize">
  <i className="fas fa-code
"></i> {job.job_code}
</p>


                    </div>
                    <div className="utf_apply_job_btn_item">
                      <a
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleJobClick(job.id, job.jobTitle, job.job_code);
                        }}
                        className="btn job-browse-btn btn-radius br-light"
                      >
                        Apply
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
          
          <div className="row">
            <div className="col-12 text-center mt-3">
              <button
                onClick={() => navigate("/browse-jobs")}
                className="btn theme-btn btn-m"
              >
                Browse All Jobs
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  };

  // Function to render company chunk with 6 companies (3 per row)
  const renderCompanyChunk = (companyChunk, chunkIndex) => {
    // Split the chunk into rows of 3 companies each
    const companyRows = chunkArray(companyChunk, 4);
    
    return (
      <section className="utf_job_category_area py-5" style={{ backgroundColor: "#f8f9fa" }} key={`company-section-${chunkIndex}`}>
        <div className="container">
          <div className="row mb-4">
            <div className="col-12 text-center">
              <div className="heading">
                <h2 className="fw-bold">Featured Companies</h2>
                <p className="text-muted">Discover job opportunities with top employers in your industry</p>
              </div>
            </div>
          </div>
          
          {companyRows.map((row, rowIndex) => (
            <div className="row mb-4" key={`company-row-${chunkIndex}-${rowIndex}`}>
             {row.map((company) => (
  <div
    key={company.company_id}
    className="col-lg-3 col-md-4 mb-4"
    role="button"
    onClick={() => handleCompanyClick(company.company_id, company.name)}
  >
    <div className="card h-100 border-0 shadow-sm">
      
      {/* Company Logo at the Top */}
      {company.image_url && company.image_url.startsWith('data:') ? (
  <img
    src={company.image_url}
    alt={company.name}
    onError={(e) => {
      e.target.style.display = "none";
      const fallbackContainer = e.target.parentNode.querySelector(".fallback-icon");
      if (fallbackContainer) {
        fallbackContainer.style.display = "flex";
      }
    }}
    className="card-img-top"
    style={{ height: "150px", objectFit: "cover" }}
  />
) : company.image_url ? (
  <div style={{ position: "relative", height: "150px" }}>
    {/* Fallback icon container - hidden by default */}
    <div
      className="fallback-icon d-flex align-items-center justify-content-center"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "#f0f0f0",
        display: "none",
        zIndex: 1
      }}
    >
      <i className="fa-solid fa-building fa-4x text-success"></i>
    </div>
    <img
      src={company.image_url}
      alt={company.name}
      loading="lazy"
      onError={(e) => {
        e.target.style.display = "none";
        const fallbackContainer = e.target.previousSibling;
        if (fallbackContainer) {
          fallbackContainer.style.display = "flex";
        }
      }}
      className="card-img-top"
      style={{
        height: "150px",
        objectFit: "cover",
        width: "100%",
        position: "absolute",
        zIndex: 2
      }}
    />
  </div>
) : (
  <div
    className="fallback-icon d-flex align-items-center justify-content-center"
    style={{
      height: "150px",
      backgroundColor: "#f0f0f0",
      display: "flex"
    }}
  >
    <i className="fa-solid fa-building fa-4x text-success"></i>
  </div>
)}

      
      <div className="card-body d-flex flex-column text-center justify-content-between">
        <div>
          <h5 className="card-title mb-1">{company.name}</h5>
          <p className="card-subtitle text-muted small">{company.sector}</p>
        </div>
      </div>
    </div>
  </div>
))}

            </div>
             
          ))}
           <div className="row">
            <div className="col-12 text-center mt-3">
              <Link to="/companies" className="btn theme-btn btn-m">
                View All Companies
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  };

  return (
    <>
      <Header />

  {/* Add this in your HTML <head> once if not already included */}
{/* <link
  rel="stylesheet"
  href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css"
/> */}

<div
  style={{
    backgroundImage: `url(${banner})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    minHeight: "10vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "30px",
  }}
>
  <div
    style={{
      background: "rgba(255, 255, 255, 0.15)",
      borderRadius: "15px",
      padding: "10px 40px",
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",
      maxWidth: "960px",
      width: "100%",
      textAlign: "center",
      color: "#fff",
      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
      border: "1px solid rgba(255, 255, 255, 0.18)",
    }}
  >
    <h1 style={{ fontSize: "2.0rem", fontWeight: 700, marginBottom: "20px" }}>
      Find Your Dream Job Today
    </h1>
    <p
      style={{
        fontSize: "1.1rem",
        color:'#334e6f',
        marginBottom: "40px",
        lineHeight: "1.6",
      }}
    >
      Discover thousands of job opportunities from top companies across India
    </p>

    <div className="row justify-content-center text-white mb-4">
      {[
        {
          icon: "fas fa-briefcase",
          label: "Job Listings",
          count: jobsData ? jobsData.length : 0,
        },
        {
          icon: "fas fa-building",
          label: "Companies",
          count: companies ? companies.length : 0,
        },
       
      ].map((item, index) => (
        <div className="col-md-3 col-6 mb-4" key={index}>
          <div>
            <div
              style={{
                width: "60px",
                height: "60px",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 15px",
              }}
            >
              <i
                className={item.icon}
                style={{ fontSize: "1.5rem", color: "#334e6f" }}
              ></i>
            </div>
            <h2
              style={{
                fontSize: "1.7rem",
                fontWeight: "bold",
                marginBottom: "5px",
              }}
            >
              {item.count}+
            </h2>
            <p style={{ fontSize: "0.95rem", margin: 0 ,color:"#334e6f"}}>{item.label}</p>
          </div>
        </div>
      ))}
    </div>

    <button
      className="btn btn-dark"
      style={{
        fontSize: "1rem",
        padding: "10px 24px",
        borderRadius: "8px",
        backgroundColor: "#212529",
        border: "none",
        transition: "all 0.3s",
      }}
      onClick={() => navigate("/browse-jobs")}
    >
      <i className="fas fa-search me-2"></i> Browse Jobs
    </button>
  </div>
</div>





      <section className="utf_job_category_area">
  <div className="container">
    <div className="row">
      <div className="col-12 text-center">
        <div className="heading">
          <h2>Job Categories</h2>
          <p>
            Browse jobs by category and find the perfect position for your
            next career move
          </p>
        </div>
      </div>
    </div>
    <div className="row">
      {isLoading ? (
        <div className="col-12 text-center">Loading categories...</div>
      ) : categories.filter((cat) => cat.jobs > 0).length === 0 ? (
        <div className="col-12 text-center">No categories available</div>
      ) : (
        categories
          .filter((category) => category.jobs > 0)
          .map((category, index) => (
            <div key={index} className="col-12 col-sm-6 col-md-3 mb-4">
              <div
                className="utf_category_box_area"
                onClick={() => handleCategoryClick(category.urlSlug)}
                style={{ cursor: "pointer" }}
              >
                <div className="utf_category_desc">
                  <div className="utf_category_icon">
                    <i
                      className={`fa-solid ${category.icon}`}
                      aria-hidden="true"
                    ></i>
                  </div>
                  <div className="category-detail utf_category_desc_text">
                    <h4>{category.displayName}</h4>
                    <p>
                      {category.jobs} {category.jobs === 1 ? "Job" : "Jobs"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))
      )}
      {categories.filter((cat) => cat.jobs > 0).length > 0 && (
        <div className="col-12 text-center mt-3">
          <button
            onClick={() => navigate("/categories")}
            className="btn theme-btn btn-m"
          >
            View All Categories
          </button>
        </div>
      )}
    </div>
  </div>
</section>


      {/* Alternating sections of Jobs and Companies */}
      {isLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-success" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading content...</p>
        </div>
      ) : (
        <>
          {jobChunks.map((jobChunk, index) => (
            <React.Fragment key={`chunk-group-${index}`}>
              {renderJobChunk(jobChunk, index)}
              {index < companyChunks.length && renderCompanyChunk(companyChunks[index], index)}
            </React.Fragment>
          ))}
        </>
      )}

      <Footer />
    </>
  );
};

export default Home;
